/** A raw index document: snake_case keys, values stored as the index stores them (raw enums, integer cents, ISO dates). */
export type DemoIndexSource = Record<string, unknown>;

export interface DemoIndexDoc {
  id: string;
  source: DemoIndexSource;
}

/** The export service's ColumnFormatter config (tio-api Search::ColumnFormatter). */
export interface DemoColumnFormat {
  type?: string;
  dateFormat?: string;
  timeFormat?: string;
  decimals?: number;
  useThousandsSeparator?: boolean;
  currency?: string;
  divisor?: number;
  percent?: boolean;
  valueMap?: Record<string, string>;
}

type Json = Record<string, unknown>;

const DAY_MS = 86_400_000;
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const ISO_INSTANT_RE = /^\d{4}-\d{2}-\d{2}T/;

const isRecord = (value: unknown): value is Json =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const asArray = (value: unknown): unknown[] =>
  Array.isArray(value) ? value : value === undefined || value === null ? [] : [value];

const firstEntry = (value: unknown): [string, unknown] | undefined =>
  isRecord(value) ? Object.entries(value)[0] : undefined;

/** Rails `camelize(:lower)`, which the search endpoint applies to every response key. */
export function camelizeKey(key: string): string {
  const camel = key
    .replace(/^_+/, '')
    .replace(/_+([a-zA-Z0-9])/g, (_match, ch: string) => ch.toUpperCase());
  return camel.charAt(0).toLowerCase() + camel.slice(1);
}

function camelizeDeep(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(camelizeDeep);
  }
  if (!isRecord(value)) {
    return value;
  }
  return Object.fromEntries(
    Object.entries(value).map(([key, inner]) => [camelizeKey(key), camelizeDeep(inner)])
  );
}

function fieldValues(source: DemoIndexSource, field: string): unknown[] {
  if (field in source) {
    return asArray(source[field]);
  }
  let current: unknown = source;
  for (const segment of field.split('.')) {
    current = isRecord(current) ? current[segment] : undefined;
  }
  return asArray(current);
}

function toEpoch(value: unknown): number | undefined {
  if (typeof value === 'number') {
    return value;
  }
  if (typeof value !== 'string') {
    return undefined;
  }
  if (ISO_DATE_RE.test(value)) {
    return Date.parse(`${value}T00:00:00Z`);
  }
  if (ISO_INSTANT_RE.test(value)) {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? undefined : parsed;
  }
  return undefined;
}

// A date-only bound rounds up for gt/lte and down for gte/lt, as OpenSearch fills missing date components.
function boundEpoch(bound: unknown, roundUp: boolean): number | undefined {
  if (typeof bound === 'string' && ISO_DATE_RE.test(bound)) {
    const start = Date.parse(`${bound}T00:00:00Z`);
    return roundUp ? start + DAY_MS - 1 : start;
  }
  return toEpoch(bound);
}

function compareToBound(value: unknown, bound: unknown, roundUp: boolean): number | undefined {
  if (typeof value === 'number') {
    const numeric = Number(bound);
    return Number.isNaN(numeric) ? undefined : value - numeric;
  }
  const valueEpoch = toEpoch(value);
  const boundValue = boundEpoch(bound, roundUp);
  if (valueEpoch !== undefined && boundValue !== undefined) {
    return valueEpoch - boundValue;
  }
  if (typeof value === 'string' && typeof bound === 'string') {
    return value < bound ? -1 : value > bound ? 1 : 0;
  }
  return undefined;
}

const RANGE_CHECKS: Array<[string, (diff: number) => boolean, boolean]> = [
  ['gte', (diff) => diff >= 0, false],
  ['gt', (diff) => diff > 0, true],
  ['lte', (diff) => diff <= 0, true],
  ['lt', (diff) => diff < 0, false],
];

function matchesRange(values: unknown[], spec: unknown): boolean {
  if (!isRecord(spec)) {
    return true;
  }
  return values.some((value) =>
    RANGE_CHECKS.every(([key, passes, roundUp]) => {
      if (spec[key] === undefined || spec[key] === null) {
        return true;
      }
      const diff = compareToBound(value, spec[key], roundUp);
      return diff !== undefined && passes(diff);
    })
  );
}

function wildcardRegExp(pattern: string, caseInsensitive: boolean): RegExp {
  const escaped = pattern.replace(/[.+^${}()|[\]\\]/g, '\\$&');
  const body = escaped.replace(/\*/g, '.*').replace(/\?/g, '.');
  return new RegExp(`^${body}$`, caseInsensitive ? 'i' : '');
}

// A leaf clause's field and operand, unwrapping the `{ field: { value } }` long form.
function leafSpec(spec: unknown): { field: string; operand: unknown; options: Json } | undefined {
  const entry = firstEntry(spec);
  if (!entry) {
    return undefined;
  }
  const [field, value] = entry;
  return isRecord(value)
    ? { field, operand: value['value'] ?? value['query'], options: value }
    : { field, operand: value, options: {} };
}

const containsText = (values: unknown[], text: unknown) =>
  typeof text === 'string' &&
  values.some((value) => String(value).toLowerCase().includes(text.toLowerCase()));

function matchesLeaf(source: DemoIndexSource, type: string, spec: unknown): boolean {
  const leaf = leafSpec(spec);
  if (!leaf) {
    return true;
  }
  const values = fieldValues(source, leaf.field);
  switch (type) {
    case 'term':
      return values.some((value) => String(value) === String(leaf.operand));
    case 'terms': {
      const wanted = asArray(leaf.operand).map(String);
      return values.some((value) => wanted.includes(String(value)));
    }
    case 'range':
      return matchesRange(values, leaf.options);
    case 'wildcard': {
      const pattern = wildcardRegExp(
        String(leaf.operand),
        Boolean(leaf.options['case_insensitive'])
      );
      return values.some((value) => pattern.test(String(value)));
    }
    case 'prefix':
      return values.some((value) => String(value).startsWith(String(leaf.operand)));
    default:
      return containsText(values, leaf.operand);
  }
}

function matchesClause(source: DemoIndexSource, clause: unknown): boolean {
  const entry = firstEntry(clause);
  if (!entry) {
    return true;
  }
  const [type, spec] = entry;
  switch (type) {
    case 'bool':
      return matchesBool(source, spec);
    case 'match_none':
      return false;
    case 'exists': {
      const field = isRecord(spec) ? spec['field'] : undefined;
      return (
        typeof field !== 'string' ||
        fieldValues(source, field).some((value) => value !== null && value !== undefined)
      );
    }
    case 'multi_match': {
      if (!isRecord(spec)) {
        return true;
      }
      const fields = asArray(spec['fields']).map((field) => String(field).replace(/\^.*$/, ''));
      const searched = fields.includes('*') ? Object.keys(source) : fields;
      return searched.some((field) => containsText(fieldValues(source, field), spec['query']));
    }
    case 'nested':
      return isRecord(spec) ? matchesClause(source, spec['query']) : true;
    case 'term':
    case 'terms':
    case 'range':
    case 'wildcard':
    case 'prefix':
    case 'match':
    case 'match_phrase':
    case 'match_phrase_prefix':
      return matchesLeaf(source, type, spec);
    default:
      return true;
  }
}

function matchesBool(source: DemoIndexSource, spec: unknown): boolean {
  if (!isRecord(spec)) {
    return true;
  }
  const all = (clauses: unknown) =>
    asArray(clauses).every((clause) => matchesClause(source, clause));
  if (!all(spec['must']) || !all(spec['filter'])) {
    return false;
  }
  if (asArray(spec['must_not']).some((clause) => matchesClause(source, clause))) {
    return false;
  }
  const should = asArray(spec['should']);
  if (should.length === 0) {
    return true;
  }
  const hasRequired = asArray(spec['must']).length > 0 || asArray(spec['filter']).length > 0;
  const minimum = Number(spec['minimum_should_match'] ?? (hasRequired ? 0 : 1));
  return should.filter((clause) => matchesClause(source, clause)).length >= minimum;
}

/** Whether a raw index document satisfies an OpenSearch query (term/terms/range/exists/wildcard/prefix/match/bool); unknown clauses match. */
export function matchesQuery(source: DemoIndexSource, query: unknown): boolean {
  return query === undefined || query === null ? true : matchesClause(source, query);
}

function compareValues(a: unknown, b: unknown): number {
  if (typeof a === 'number' && typeof b === 'number') {
    return a - b;
  }
  const aEpoch = toEpoch(a);
  const bEpoch = toEpoch(b);
  if (aEpoch !== undefined && bEpoch !== undefined) {
    return aEpoch - bEpoch;
  }
  const aText = String(a);
  const bText = String(b);
  return aText < bText ? -1 : aText > bText ? 1 : 0;
}

interface SortClause {
  field: string;
  descending: boolean;
  missingFirst: boolean;
}

function sortClauses(sort: unknown): SortClause[] {
  return asArray(sort).flatMap((entry): SortClause[] => {
    if (typeof entry === 'string') {
      return [{ field: entry, descending: false, missingFirst: false }];
    }
    const pair = firstEntry(entry);
    if (!pair) {
      return [];
    }
    const [field, spec] = pair;
    const order = isRecord(spec) ? spec['order'] : spec;
    const missing = isRecord(spec) ? spec['missing'] : undefined;
    return [{ field, descending: order === 'desc', missingFirst: missing === '_first' }];
  });
}

function sortDocs(docs: DemoIndexDoc[], sort: unknown): DemoIndexDoc[] {
  const clauses = sortClauses(sort).filter(
    (clause) => clause.field !== '_score' && clause.field !== '_doc'
  );
  if (clauses.length === 0) {
    return docs;
  }
  const sortValue = (doc: DemoIndexDoc, clause: SortClause) => {
    if (clause.field === '_id') {
      return doc.id;
    }
    const values = fieldValues(doc.source, clause.field)
      .filter((value) => value !== null && value !== undefined)
      .sort(compareValues);
    return clause.descending ? values[values.length - 1] : values[0];
  };
  return [...docs].sort((a, b) => {
    for (const clause of clauses) {
      const aValue = sortValue(a, clause);
      const bValue = sortValue(b, clause);
      if (aValue === undefined || bValue === undefined) {
        if (aValue === bValue) {
          continue;
        }
        const aFirst = aValue === undefined ? -1 : 1;
        return clause.missingFirst ? aFirst : -aFirst;
      }
      const diff = compareValues(aValue, bValue);
      if (diff !== 0) {
        return clause.descending ? -diff : diff;
      }
    }
    return 0;
  });
}

function presentValues(rows: DemoIndexSource[], field: string): unknown[] {
  return rows.flatMap((row) =>
    fieldValues(row, field).filter((value) => value !== null && value !== undefined && value !== '')
  );
}

function numericValues(rows: DemoIndexSource[], field: string): number[] {
  return presentValues(rows, field).flatMap((value) => {
    const numeric =
      typeof value === 'boolean'
        ? Number(value)
        : typeof value === 'number'
          ? value
          : toEpoch(value);
    return numeric === undefined ? [] : [numeric];
  });
}

function metricAgg(rows: DemoIndexSource[], type: string, spec: Json): Json | undefined {
  const field = typeof spec['field'] === 'string' ? spec['field'] : undefined;
  if (!field) {
    return undefined;
  }
  const present = presentValues(rows, field);
  const numbers = numericValues(rows, field);
  const isDate = present.some((value) => typeof value === 'string');
  const extreme = (value: number | undefined): Json =>
    value === undefined
      ? { value: null }
      : isDate
        ? { value, valueAsString: new Date(value).toISOString() }
        : { value };
  const total = numbers.reduce((sum, value) => sum + value, 0);
  switch (type) {
    case 'value_count':
      return { value: present.length };
    case 'cardinality':
      return { value: new Set(present.map(String)).size };
    case 'sum':
      return { value: total };
    case 'avg':
      return { value: numbers.length ? total / numbers.length : null };
    case 'min':
      return extreme(numbers.length ? Math.min(...numbers) : undefined);
    case 'max':
      return extreme(numbers.length ? Math.max(...numbers) : undefined);
    default:
      return undefined;
  }
}

// Terms on a boolean key by 1/0 with the true/false string alongside, as OpenSearch does.
function bucketKey(value: unknown): Json {
  return typeof value === 'boolean'
    ? { key: value ? 1 : 0, keyAsString: String(value) }
    : { key: value };
}

function termsAgg(rows: DemoIndexSource[], spec: Json, subAggs: unknown): Json {
  const field = String(spec['field'] ?? '');
  const groups = new Map<string, { value: unknown; rows: DemoIndexSource[] }>();
  for (const row of rows) {
    const distinct = new Set(presentValues([row], field).map((value) => JSON.stringify(value)));
    for (const encoded of distinct) {
      const group = groups.get(encoded) ?? { value: JSON.parse(encoded) as unknown, rows: [] };
      group.rows.push(row);
      groups.set(encoded, group);
    }
  }
  const requested = asArray(spec['order']).flatMap((order) =>
    isRecord(order) ? Object.entries(order) : []
  );
  const orders = requested.length
    ? requested
    : [
        ['_count', 'desc'],
        ['_key', 'asc'],
      ];
  const sorted = [...groups.values()].sort((a, b) => {
    for (const [by, direction] of orders) {
      const diff =
        by === '_count' ? a.rows.length - b.rows.length : compareValues(a.value, b.value);
      if (diff !== 0) {
        return direction === 'desc' ? -diff : diff;
      }
    }
    return 0;
  });
  const size = Number(spec['size'] ?? 10);
  const others = sorted.slice(size).reduce((sum, group) => sum + group.rows.length, 0);
  return {
    docCountErrorUpperBound: 0,
    sumOtherDocCount: others,
    buckets: sorted.slice(0, size).map((group) => ({
      ...bucketKey(group.value),
      docCount: group.rows.length,
      ...(isRecord(subAggs) ? runAggregations(group.rows, subAggs) : {}),
    })),
  };
}

function histogramStart(epoch: number, interval: string): number {
  const date = new Date(epoch);
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth();
  const day = Date.UTC(year, month, date.getUTCDate());
  switch (interval) {
    case 'year':
      return Date.UTC(year, 0, 1);
    case 'quarter':
      return Date.UTC(year, month - (month % 3), 1);
    case 'month':
      return Date.UTC(year, month, 1);
    case 'week':
      return day - ((new Date(day).getUTCDay() + 6) % 7) * DAY_MS;
    default:
      return day;
  }
}

// The Java date patterns the report builder sends as a date_histogram `format`.
function formatHistogramKey(epoch: number, format: unknown): unknown {
  if (typeof format !== 'string') {
    return epoch;
  }
  const date = new Date(epoch);
  const pad = (value: number) => String(value).padStart(2, '0');
  return format
    .replace('yyyy', String(date.getUTCFullYear()))
    .replace('QQQ', `Q${Math.floor(date.getUTCMonth() / 3) + 1}`)
    .replace('MM', pad(date.getUTCMonth() + 1))
    .replace('dd', pad(date.getUTCDate()));
}

interface CompositeSource {
  name: string;
  descending: boolean;
  keysFor: (row: DemoIndexSource) => unknown[];
}

function compositeSources(sources: unknown): CompositeSource[] {
  return asArray(sources).flatMap((source): CompositeSource[] => {
    const named = firstEntry(source);
    const kind = named ? firstEntry(named[1]) : undefined;
    if (!named || !kind || !isRecord(kind[1])) {
      return [];
    }
    const spec = kind[1];
    const field = String(spec['field'] ?? '');
    const descending = spec['order'] === 'desc';
    if (kind[0] === 'date_histogram') {
      const interval = String(spec['calendar_interval'] ?? spec['interval'] ?? 'day');
      const keysFor = (row: DemoIndexSource) =>
        numericValues([row], field).map((epoch) =>
          formatHistogramKey(histogramStart(epoch, interval), spec['format'])
        );
      return [{ name: named[0], descending, keysFor }];
    }
    return [{ name: named[0], descending, keysFor: (row) => presentValues([row], field) }];
  });
}

function compositeAgg(rows: DemoIndexSource[], spec: Json, subAggs: unknown): Json {
  const sources = compositeSources(spec['sources']);
  const groups = new Map<string, { key: unknown[]; rows: DemoIndexSource[] }>();
  for (const row of rows) {
    let combos: unknown[][] = [[]];
    for (const source of sources) {
      combos = combos.flatMap((combo) => source.keysFor(row).map((key) => [...combo, key]));
    }
    for (const combo of combos) {
      const encoded = JSON.stringify(combo);
      const group = groups.get(encoded) ?? { key: combo, rows: [] };
      group.rows.push(row);
      groups.set(encoded, group);
    }
  }
  const compareKeys = (a: unknown[], b: unknown[]) => {
    for (const [index, source] of sources.entries()) {
      const diff = compareValues(a[index], b[index]);
      if (diff !== 0) {
        return source.descending ? -diff : diff;
      }
    }
    return 0;
  };
  const after = isRecord(spec['after']) ? spec['after'] : undefined;
  const afterKey = after
    ? sources.map((source) => after[source.name] ?? after[camelizeKey(source.name)])
    : undefined;
  const page = [...groups.values()]
    .sort((a, b) => compareKeys(a.key, b.key))
    .filter((group) => !afterKey || compareKeys(group.key, afterKey) > 0)
    .slice(0, Number(spec['size'] ?? 10));
  const keyObject = (key: unknown[]) =>
    Object.fromEntries(sources.map((source, index) => [camelizeKey(source.name), key[index]]));
  const last = page[page.length - 1];
  return {
    ...(last ? { afterKey: keyObject(last.key) } : {}),
    buckets: page.map((group) => ({
      key: keyObject(group.key),
      docCount: group.rows.length,
      ...(isRecord(subAggs) ? runAggregations(group.rows, subAggs) : {}),
    })),
  };
}

/** Evaluates terms, composite, filter, top-hit and metric aggregations over matched documents, camelizing names as the API does; other kinds are omitted. */
export function runAggregations(rows: DemoIndexSource[], aggs: Json): Json {
  const result: Json = {};
  for (const [name, definition] of Object.entries(aggs)) {
    if (!isRecord(definition)) {
      continue;
    }
    const subAggs = definition['aggs'] ?? definition['aggregations'];
    const kind = Object.entries(definition).find(
      ([key]) => key !== 'aggs' && key !== 'aggregations'
    );
    if (!kind || !isRecord(kind[1])) {
      continue;
    }
    const [type, spec] = kind;
    let value: Json | undefined;
    if (type === 'terms') {
      value = termsAgg(rows, spec, subAggs);
    } else if (type === 'composite') {
      value = compositeAgg(rows, spec, subAggs);
    } else if (type === 'top_hits') {
      const docs = rows.map((source, index) => ({ id: String(source['id'] ?? index), source }));
      const page = sortDocs(docs, spec['sort']).slice(0, Number(spec['size'] ?? 3));
      value = {
        hits: {
          total: { value: rows.length, relation: 'eq' },
          hits: page.map((doc) => ({
            _id: doc.id,
            _source: camelizeDeep(projectSource(doc.source, spec['_source'])),
          })),
        },
      };
    } else if (type === 'filter') {
      const kept = rows.filter((row) => matchesQuery(row, spec));
      value = {
        docCount: kept.length,
        ...(isRecord(subAggs) ? runAggregations(kept, subAggs) : {}),
      };
    } else {
      value = metricAgg(rows, type, spec);
    }
    if (value) {
      result[camelizeKey(name)] = value;
    }
  }
  return result;
}

function projectSource(source: DemoIndexSource, selection: unknown): DemoIndexSource {
  const includes = isRecord(selection) ? selection['includes'] : selection;
  if (!Array.isArray(includes) || includes.length === 0) {
    return source;
  }
  const wanted = new Set(includes.map(String));
  return Object.fromEntries(Object.entries(source).filter(([key]) => wanted.has(key)));
}

/** Answers an OpenSearch body over in-memory documents (query, post_filter, sort, from/size, _source, aggregations), camelized as `/api/v2/search` returns it. */
export function runDemoSearch(docs: DemoIndexDoc[], body: unknown, indexName: string) {
  const search = isRecord(body) ? body : {};
  const matched = docs.filter((doc) => matchesQuery(doc.source, search['query']));
  const visible = matched.filter((doc) => matchesQuery(doc.source, search['post_filter']));
  const from = Math.max(0, Number(search['from'] ?? 0) || 0);
  const size = Math.max(0, Number(search['size'] ?? 10) || 0);
  const page = sortDocs(visible, search['sort']).slice(from, from + size);
  const aggs = search['aggs'] ?? search['aggregations'];
  return {
    took: 2,
    timedOut: false,
    hits: {
      total: { value: visible.length, relation: 'eq' },
      maxScore: null,
      hits: page.map((doc) => ({
        _index: indexName,
        _id: doc.id,
        _score: null,
        _source: camelizeDeep(projectSource(doc.source, search['_source'])),
      })),
    },
    ...(isRecord(aggs)
      ? {
          aggregations: runAggregations(
            matched.map((doc) => doc.source),
            aggs
          ),
        }
      : {}),
  };
}

/** Every document matching a body's query in index order, as an export's scroll reads them (paging and sort ignored). */
export function scrollDemoSearch(docs: DemoIndexDoc[], body: unknown): DemoIndexSource[] {
  const query = isRecord(body) ? body['query'] : undefined;
  return docs.filter((doc) => matchesQuery(doc.source, query)).map((doc) => doc.source);
}

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function formatDateCell(raw: unknown, format: DemoColumnFormat): string {
  const epoch = toEpoch(raw);
  if (epoch === undefined) {
    return String(raw);
  }
  const date = new Date(epoch);
  const pad = (value: number) => String(value).padStart(2, '0');
  const year = date.getUTCFullYear();
  const monthName = MONTHS[date.getUTCMonth()] ?? '';
  const mm = pad(date.getUTCMonth() + 1);
  const dd = pad(date.getUTCDate());
  const dateParts: Record<string, string> = {
    'MM-DD-YYYY': `${mm}-${dd}-${year}`,
    'DD/MM/YYYY': `${dd}/${mm}/${year}`,
    'YYYY-MM-DD': `${year}-${mm}-${dd}`,
    'MMM DD, YYYY': `${monthName.slice(0, 3)} ${dd}, ${year}`,
    'MMMM DD, YYYY': `${monthName} ${dd}, ${year}`,
  };
  const datePart = dateParts[format.dateFormat ?? ''] ?? `${mm}/${dd}/${year}`;
  const hours = date.getUTCHours();
  const h12 = pad(hours % 12 === 0 ? 12 : hours % 12);
  const period = hours >= 12 ? 'PM' : 'AM';
  const minutes = pad(date.getUTCMinutes());
  const seconds = pad(date.getUTCSeconds());
  const timeParts: Record<string, string> = {
    'HH:mm': `${pad(hours)}:${minutes}`,
    'HH:mm:ss': `${pad(hours)}:${minutes}:${seconds}`,
    'hh:mm A': `${h12}:${minutes} ${period}`,
    'hh:mm:ss A': `${h12}:${minutes}:${seconds} ${period}`,
  };
  const timePart = timeParts[format.timeFormat ?? ''];
  return timePart ? `${datePart} ${timePart}` : datePart;
}

function formatNumberCell(raw: unknown, format: DemoColumnFormat): string {
  let numeric = typeof raw === 'number' ? raw : Number(raw);
  if (Number.isNaN(numeric)) {
    return String(raw);
  }
  if (format.divisor) {
    numeric /= format.divisor;
  }
  if (format.percent) {
    numeric *= 100;
  }
  const [integer = '', fraction] = numeric.toFixed(format.decimals ?? 0).split('.');
  const grouped = format.useThousandsSeparator
    ? integer.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')
    : integer;
  const formatted = fraction ? `${grouped}.${fraction}` : grouped;
  if (format.percent) {
    return `${formatted}%`;
  }
  const prefix: Record<string, string> = { USD: '$', CAD: 'C$' };
  return `${prefix[format.currency ?? ''] ?? ''}${formatted}`;
}

function plainCell(value: unknown): string {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  if (Array.isArray(value)) {
    return value
      .map(plainCell)
      .filter((cell) => cell.trim() !== '')
      .join('\n---\n');
  }
  if (isRecord(value)) {
    return Object.entries(value)
      .map(([key, inner]) => [key, plainCell(inner)] as const)
      .filter(([, cell]) => cell.trim() !== '')
      .map(([key, cell]) => `${key}=${cell}`)
      .join('\n');
  }
  return String(value);
}

/** One CSV cell as the export's ColumnFormatter renders it: dates, numbers (divisor, currency, percent), enum labels, else the raw value. */
export function formatExportCell(value: unknown, format?: DemoColumnFormat): string {
  if (!format || value === null || value === undefined || String(value).trim() === '') {
    return plainCell(value);
  }
  switch (format.type) {
    case 'date':
      return formatDateCell(value, format);
    case 'number':
      return formatNumberCell(value, format);
    case 'enum':
      return format.valueMap?.[String(value)] ?? String(value);
    default:
      return plainCell(value);
  }
}

// Ruby's CSV quotes empty strings and any field holding a separator, quote or newline.
function csvField(cell: string): string {
  return cell === '' || /[",\r\n]/.test(cell) ? `"${cell.replace(/"/g, '""')}"` : cell;
}

/** The export file body: a UTF-8 BOM, the header row, then one newline-terminated line per row. */
export function buildExportCsv(header: string[], rows: string[][]): string {
  return `\uFEFF${[header, ...rows].map((cells) => `${cells.map(csvField).join(',')}\n`).join('')}`;
}
